import { useMemo, useState } from 'react';

import { getUserData } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { answerOf, findAnswer, type PresenceAnswer, setAnswer, tallyPresence } from './agent-presence';
import { PartyAvatar } from './party-avatar';
import { PresenceBulkBar } from './presence-bulk-bar';
import { PresenceToggle } from './presence-toggle';
import { useAgentPresenceSeed } from './use-agent-presence';
import { sortForDisplay } from './utils';

type Filter = 'all' | PresenceAnswer;

const FILTERS: { value: Filter; label: string }[] = [
	{ value: 'all', label: 'All' },
	{ value: 'unrecorded', label: 'Not recorded' },
	{ value: 'present', label: 'Present' },
	{ value: 'absent', label: 'No agent' },
];

/**
 * "Polling Agents" — the whole column four on one screen, for the agent who would rather do
 * it as a second pass than card by card. Same answers, same state as the inline control.
 *
 * Rows stay in the grid's display order (ballot order for a real ballot, otherwise sheet
 * order by shortName) whatever the filter; re-sorting by answer would stop the list
 * matching the paper the agent is reading from.
 */
const PollingAgentsStage = () => {
	const {
		global: {
			state: { partyVotes, partyBallot, selectedParentCode, agentPresence },
			updateState,
		},
	} = useGlobalContext();
	const [filter, setFilter] = useState<Filter>('all');

	const roster = useMemo(() => {
		const entry = partyVotes.find((p) => p.id === selectedParentCode?.codeId);
		return sortForDisplay(entry?.votes || [], partyBallot);
	}, [partyVotes, partyBallot, selectedParentCode?.codeId]);
	const rosterIds = useMemo(() => roster.map((v) => v.partyId), [roster]);

	const { readStatus } = useAgentPresenceSeed(rosterIds);
	const tally = useMemo(() => tallyPresence(agentPresence, rosterIds), [agentPresence, rosterIds]);
	const ownPartyId = Number(getUserData().user?.PartyId) || 0;
	const ownAnswer = answerOf(agentPresence, ownPartyId);
	const ownOnBallot = rosterIds.includes(ownPartyId);

	const shown = filter === 'all' ? roster : roster.filter((v) => answerOf(agentPresence, v.partyId) === filter);

	if (!roster.length) {
		return (
			<div className='pv-empty'>
				<p className='pv-empty__title'>No parties loaded yet</p>
				<p className='pv-empty__text'>Open Party Vote Count first so the party list loads.</p>
			</div>
		);
	}

	return (
		<div className='pa-stage'>
			<PresenceBulkBar
				rosterIds={rosterIds}
				tally={tally}
				readStatus={readStatus}
			/>

			{ownOnBallot && ownAnswer !== 'present' ? (
				<p
					className='pa-stage__insight'
					role='status'
				>
					{ownAnswer === 'absent'
						? 'Your own party is marked as having no agent here. If you are at this unit, mark it present.'
						: 'Your own party has no answer yet.'}
				</p>
			) : null}

			<div
				className='pa-filters'
				role='group'
				aria-label='Show parties'
			>
				{FILTERS.map((f) => {
					const count = f.value === 'all' ? tally.total : tally[f.value];
					return (
						<button
							key={f.value}
							type='button'
							aria-pressed={filter === f.value}
							className={`pa-filter${filter === f.value ? ' is-selected' : ''}`}
							onClick={() => setFilter(f.value)}
						>
							{f.label} <span className='pa-filter__count'>{count}</span>
						</button>
					);
				})}
			</div>

			{shown.length ? (
				<ul className='pa-list'>
					{shown.map((vote) => {
						const a = findAnswer(agentPresence, vote.partyId);
						const answer = a?.answer ?? 'unrecorded';
						return (
							<li
								key={vote.partyId}
								className={`pa-row pa-row--${answer}`}
							>
								<div className='pa-row__party'>
									<PartyAvatar vote={vote} />
									<div className='pa-row__names'>
										<span className='pa-row__abbr'>{vote.label}</span>
										{vote.longName ? <span className='pa-row__long'>{vote.longName}</span> : null}
									</div>
									<span className='pa-row__votes'>{Number(vote.votes) || 0} votes</span>
								</div>
								<PresenceToggle
									partyLabel={vote.label}
									answer={answer}
									agentName={a?.agentName || ''}
									note={a?.note || ''}
									isOwnParty={vote.partyId === ownPartyId}
									showNote
									onAnswer={(next) => updateState('agentPresence', setAnswer(agentPresence, vote.partyId, { answer: next }))}
									onAgentName={(agentName) => updateState('agentPresence', setAnswer(agentPresence, vote.partyId, { agentName }))}
									onNote={(note) => updateState('agentPresence', setAnswer(agentPresence, vote.partyId, { note }))}
								/>
							</li>
						);
					})}
				</ul>
			) : (
				<p className='pa-stage__empty'>No parties in this view.</p>
			)}
		</div>
	);
};

export default PollingAgentsStage;
